/** IANA calendar helpers. Instants stay UTC; wall clocks use the dietitian zone. */

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function isUnsetTimeZone(tz) {
  const n = String(tz || "")
    .trim()
    .toUpperCase()
    .replace(/ /g, "_");
  return (
    !n ||
    n === "UTC" ||
    n === "ETC/UTC" ||
    n === "GMT" ||
    n === "ETC/GMT" ||
    n === "ETC/GMT+0" ||
    n === "ETC/GMT-0"
  );
}

/** Always the viewer's city. Do not inherit a saved company zone (e.g. Karachi). */
export function effectiveAppointmentTimeZone(_meta) {
  return browserTimeZone();
}

export function ymdInTimeZone(date = new Date(), timeZone = "UTC") {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: timeZone || "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  } catch {
    return new Date(date).toISOString().slice(0, 10);
  }
}

export function timezoneOffsetLabel(timeZone = "UTC", now = new Date()) {
  try {
    return (
      new Intl.DateTimeFormat("en-US", {
        timeZone: timeZone || "UTC",
        timeZoneName: "shortOffset",
      })
        .formatToParts(now)
        .find((p) => p.type === "timeZoneName")?.value || ""
    );
  } catch {
    return "";
  }
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function partsInZone(date, timeZone) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: timeZone || "UTC",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  const parts = fmt.formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return {
    ymd: `${get("year")}-${get("month")}-${get("day")}`,
    hour: Number(get("hour")),
    minute: Number(get("minute")),
  };
}

export function zonedLocalToUtc(ymd, hm, timeZone) {
  const match = String(hm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!match || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return new Date(NaN);
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  const [y, mo, d] = ymd.split("-").map(Number);
  let t = Date.UTC(y, mo - 1, d, hour, minute, 0);
  for (let i = 0; i < 36; i++) {
    const got = partsInZone(new Date(t), timeZone);
    if (got.ymd === ymd && got.hour === hour && got.minute === minute) {
      return new Date(t);
    }
    let delta = hour * 60 + minute - (got.hour * 60 + got.minute);
    if (got.ymd !== ymd) delta += got.ymd < ymd ? 24 * 60 : -24 * 60;
    if (delta === 0) break;
    t += delta * 60000;
  }
  return new Date(t);
}

export function formatClock12(hm) {
  const match = String(hm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return hm || "";
  let hour = Number(match[1]);
  const minute = match[2];
  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minute} ${suffix}`;
}

export function formatClockRange12(start, end) {
  const a = formatClock12(start);
  const b = formatClock12(end);
  if (!a || !b) return a || b || "";
  const aSuffix = a.slice(-2);
  const bSuffix = b.slice(-2);
  if (aSuffix === bSuffix) {
    return `${a.replace(/ [AP]M$/, "")} – ${b}`;
  }
  return `${a} – ${b}`;
}

export function clockInTimeZone(iso, timeZone) {
  if (!iso) return "";
  const p = partsInZone(new Date(iso), timeZone || "UTC");
  if (!p?.ymd) return "";
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

export function appointmentClockRange(appt, timeZone) {
  const start =
    clockInTimeZone(appt?.starts_at, timeZone) || appt?.start_time || "";
  const end = clockInTimeZone(appt?.ends_at, timeZone) || appt?.end_time || "";
  if (start && end) return `${start} – ${end}`;
  return start || "—";
}

export function weekdaySun0(ymd, timeZone) {
  const inst = zonedLocalToUtc(ymd, "12:00", timeZone);
  const short = new Intl.DateTimeFormat("en-US", {
    timeZone: timeZone || "UTC",
    weekday: "short",
  }).format(inst);
  const i = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(short);
  return i >= 0 ? i : 0;
}

export function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function monthGrid(year, month, timeZone) {
  const first = `${year}-${pad2(month)}-01`;
  const pad = weekdaySun0(first, timeZone);
  const n = daysInMonth(year, month);
  const cells = [];
  for (let i = 0; i < pad; i++) cells.push(null);
  for (let d = 1; d <= n; d++) {
    cells.push(`${year}-${pad2(month)}-${pad2(d)}`);
  }
  return cells;
}

export function shiftMonth(year, month, delta) {
  const d = new Date(Date.UTC(year, month - 1 + delta, 1));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1 };
}

export function monthLabel(year, month) {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

const CLOSED_APPOINTMENT_STATUSES = [
  "cancelled",
  "rescheduled",
  "missed",
  "completed",
];

export function appointmentInstantMs(value) {
  if (!value) return NaN;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : NaN;
}

export function appointmentEndsAtMs(a) {
  const end = appointmentInstantMs(a?.ends_at);
  if (Number.isFinite(end)) return end;
  const start = appointmentInstantMs(a?.starts_at);
  const mins = Number(a?.duration_minutes);
  if (Number.isFinite(start) && Number.isFinite(mins) && mins > 0) {
    return start + mins * 60_000;
  }
  return NaN;
}

/** True while the booked slot is open (start <= now < end). */
export function appointmentInLiveWindow(a, now = Date.now()) {
  const status = String(a?.status || "");
  if (CLOSED_APPOINTMENT_STATUSES.includes(status)) return false;
  const start = appointmentInstantMs(a?.starts_at);
  const end = appointmentEndsAtMs(a);
  if (Number.isFinite(start) && Number.isFinite(end)) {
    return start <= now && now < end;
  }
  return status === "started";
}
